/**
 * Machine-readable error code for retryable booking conflicts.
 * Must match the constant exported from the backend (createBooking.ts).
 *
 * This occurs when DynamoDB eventual consistency causes a stale read,
 * leading to an ID collision with a cancelled/inactive booking.
 */
export const BOOKING_CONFLICT_RETRY = 'BOOKING_CONFLICT_RETRY';

/**
 * Retry a function when it fails with a BOOKING_CONFLICT_RETRY error.
 *
 * DynamoDB eventual consistency can cause the booking creation query to miss
 * a recently cancelled booking, producing an ID collision. The backend returns
 * a 409 ConflictError with the BOOKING_CONFLICT_RETRY message in this case.
 * A simple retry resolves it because the stale read window is typically
 * only milliseconds.
 *
 * @param fn - The async function to call (e.g. BrowserApiClient.createBooking)
 * @param maxRetries - Maximum number of retries (default: 2)
 * @param delayMs - Delay between retries in milliseconds (default: 500)
 */
export async function retryOnBookingConflict<T>(
  fn: () => Promise<T>,
  maxRetries: number = 2,
  delayMs: number = 500
): Promise<T> {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error: any) {
      const isRetryableConflict = error.message?.includes(BOOKING_CONFLICT_RETRY);

      if (!isRetryableConflict || attempt === maxRetries) {
        throw error;
      }

      // Wait before retrying — the eventual consistency window is very short
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }

  // Unreachable — the loop always returns or throws
  throw new Error('Unexpected: retryOnBookingConflict exhausted without result');
}
